/**
 * useSpotifyPlayerState — owns all of SpotifyPlayer's local UI state +
 * its side-effecting helpers (load browse tab, run search, save default URL,
 * seek, volume) so the presentational sub-components stay dumb.
 *
 * Inputs (from parent — usually `useMusic()` + `useAuth()`):
 *   status         — /spotify/status response (connected + premium + profile)
 *   sdk            — controls object from `useSpotifySDK` (play/pause/seek/setVolume…)
 *   sdkState       — Spotify Player state ({track_window, paused, position, duration})
 *   open           — controlled `pillOpen` from MusicContext
 *   user / setUser — auth context so saveUrl can persist the default playlist
 *   setCurrent     — MusicContext setter so a saved URL becomes the current track
 *
 * Returns a single object the orchestrator can destructure into its panels.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useSwipe, useIsMobile } from "../../lib/useSwipe";
import { parseSpotifyEmbed } from "../../context/MusicContext";
import { toast } from "sonner";

export const BROWSE_TABS = [
  { key: "playlists", label: "Lists",   path: "/spotify/playlists?limit=30" },
  { key: "liked",     label: "Liked",   path: "/spotify/liked?limit=30" },
  { key: "top",       label: "Top",     path: "/spotify/top-tracks?limit=30" },
  { key: "recent",    label: "Recent",  path: "/spotify/recently-played?limit=30" },
];

export function useSpotifyPlayerState({ status, sdk, sdkState, open, user, setUser, setCurrent }) {
  // ---------- Browse tabs ----------
  const [tab, setTab] = useState("playlists");
  const [tabItems, setTabItems] = useState([]);
  const [tabLoading, setTabLoading] = useState(false);
  const isMobile = useIsMobile();
  const tabKeys = BROWSE_TABS.map((t) => t.key);
  const tabSwipe = useSwipe({
    enabled: isMobile,
    onSwipeLeft: () => {
      const i = tabKeys.indexOf(tab);
      if (i < tabKeys.length - 1) setTab(tabKeys[i + 1]);
    },
    onSwipeRight: () => {
      const i = tabKeys.indexOf(tab);
      if (i > 0) setTab(tabKeys[i - 1]);
    },
  });

  const loadTab = useCallback(async (which) => {
    if (!status?.connected) return;
    setTabLoading(true);
    try {
      const path = BROWSE_TABS.find((t) => t.key === which)?.path;
      if (!path) return;
      const { data } = await api.get(path);
      setTabItems(data.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load");
      setTabItems([]);
    } finally {
      setTabLoading(false);
    }
  }, [status?.connected]);

  useEffect(() => {
    if (open && status?.connected) loadTab(tab);
  }, [open, tab, loadTab, status?.connected]);

  // ---------- Search ----------
  const [query, setQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  const runSearch = async (e) => {
    e?.preventDefault?.();
    if (!query.trim()) {
      setSearchResults(null);
      return;
    }
    setSearching(true);
    try {
      const { data } = await api.get(`/spotify/search?q=${encodeURIComponent(query)}&limit=10`);
      setSearchResults(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Search failed");
    } finally {
      setSearching(false);
    }
  };

  const closeSearch = () => {
    setSearchOpen(false);
    setSearchResults(null);
    setQuery("");
  };

  // ---------- Default-playlist URL editor ----------
  const [urlEditing, setUrlEditing] = useState(false);
  const [url, setUrl] = useState("");
  const [savingUrl, setSavingUrl] = useState(false);

  useEffect(() => {
    setUrl(user?.spotify_playlist_url || "");
  }, [user?.spotify_playlist_url]);

  const saveUrl = async () => {
    setSavingUrl(true);
    try {
      const parsed = parseSpotifyEmbed(url);
      if (url && !parsed) {
        toast.error("Not a valid Spotify URL");
        return;
      }
      const { data } = await api.put("/auth/profile", { spotify_playlist_url: url });
      setUser(data);
      localStorage.setItem("if_user", JSON.stringify(data));
      if (parsed) setCurrent(parsed);
      setUrlEditing(false);
      toast.success(url ? "Saved as default" : "Cleared default");
    } catch {
      toast.error("Failed to save");
    } finally {
      setSavingUrl(false);
    }
  };

  // ---------- Volume + seek ticker ----------
  const [volume, setVolumeState] = useState(0.6);
  const [seekPos, setSeekPos] = useState(null);

  useEffect(() => {
    if (!sdkState || sdkState.paused) {
      setSeekPos(null);
      return undefined;
    }
    setSeekPos(sdkState.position);
    const id = setInterval(() => {
      setSeekPos((p) => (p == null ? null : p + 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [sdkState?.position, sdkState?.paused, sdkState?.track_window?.current_track?.id]);

  const onSeek = async (e) => {
    const ms = Number(e.target.value);
    setSeekPos(ms);
    await sdk.seek(ms);
  };

  const onVolume = async (e) => {
    const v = Number(e.target.value);
    setVolumeState(v);
    await sdk.setVolume(v);
  };

  return {
    // tabs
    tab, setTab, tabItems, tabLoading, tabSwipeBind: tabSwipe.bind,
    // search
    query, setQuery, searchOpen, setSearchOpen, searchResults, searching,
    runSearch, closeSearch,
    // url editor
    urlEditing, setUrlEditing, url, setUrl, savingUrl, saveUrl,
    // playback
    volume, onVolume, seekPos, onSeek,
  };
}
